import { Box, styled, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { convertNewlinesToHtml } from '@Utils/format';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import {
  CategoryMasterDataFormValues,
  IImpactsOnStakeholdersFormValues,
  StakeholderGroupFormValues,
  StakeholderType,
} from '../../ImpactsOnStakeholdersFormProvider/types';
import StakeholderAnalysisActions from './actions/StakeholderAnalysisActions';

export interface IEditModeState {
  mode: 'create' | 'edit';
  data?: StakeholderGroupFormValues;
  index?: number;
}

export type ColumnActionProps = {
  getValues: UseFormGetValues<IImpactsOnStakeholdersFormValues>;
  setValue: UseFormSetValue<IImpactsOnStakeholdersFormValues>;
};

export enum STAKEHOLDER_ANALYSIS_TABLE_COLUMNS {
  ID = 'id',
  STAKEHOLDERS = 'stakeholders',
  EXPECTATION = 'expectationDescriptionTh',
  EXPECTATION_RESPONSE = 'respondingToExpectationTh',
  CHANNEL = 'communicationActivities',
  ACTION = 'action',
}

const UnorderList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(3),
}));

const UnorderOtherList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(5),
}));

const mappingStakeholderType = (type: StakeholderType) => {
  switch (type) {
    case StakeholderType.INTERNAL:
      return 'ผู้มีส่วนได้เสียภายในองค์กร';
    case StakeholderType.EXTERNAL:
      return 'ผู้มีส่วนได้เสียภายนอกองค์กร';
    default:
      return '-';
  }
};

const getContentList = (
  contents: Array<CategoryMasterDataFormValues | CategoryMasterDataFormValues>,
  language: 'EN' | 'TH'
): Array<string> => {
  const contentList: Array<string> = [];
  contents?.forEach((content) => {
    contentList.push(
      language === 'TH' ? content.nameTh ?? '' : content.nameEn ?? ''
    );
  });
  return contentList;
};

export const STAKEHOLDER_ANALYSIS_VISIBILITY = {
  [STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.ID]: false,
};

export const getStakeholderAnalysisColumn = (
  props?: ColumnActionProps
): GridColDef[] => [
  { field: STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.ID, headerName: 'No.' },
  {
    field: STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.STAKEHOLDERS,
    headerName: 'กลุ่มผู้มีส่วนได้เสีย',
    minWidth: 175,
    colSpan: (_, row) => {
      if (row.id.includes('GROUP_HEADER')) return 4;
      return undefined;
    },
    renderCell: (field) => {
      const { row, value } = field;

      if (row.id.includes('GROUP_HEADER')) {
        const currentType = row.stakeholderType;
        const rowIndex = field.api.getRowIndexRelativeToVisibleRows(row.id);
        const previousRow =
          rowIndex > 0
            ? field.api.getRow(field.api.getRowIdFromRowIndex(rowIndex - 1))
            : null;

        if (!previousRow || previousRow.stakeholderType !== currentType) {
          return (
            <Typography fontWeight={800}>
              {mappingStakeholderType(currentType)}
            </Typography>
          );
        }
        return null;
      }

      if (value?.length === 0) return <Typography>{'-'}</Typography>;

      const hasNoStakeholder = !value && !row.otherStakeholders;
      const isEmptyStakeholder =
        value?.length === 0 && row.otherStakeholders?.length === 0;

      if (hasNoStakeholder || isEmptyStakeholder)
        return <Typography>{'-'}</Typography>;

      const stakeholderNameTh = getContentList(value, 'TH');
      const stakeholderOtherNameTh = getContentList(
        row.otherStakeholders ?? [],
        'TH'
      );
      const stakeholderNameEn = getContentList(value, 'EN');
      const stakeholderOtherNameEn = getContentList(
        row.otherStakeholders ?? [],
        'EN'
      );

      return (
        <Box>
          <UnorderList>
            {stakeholderNameTh.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderList>
          <UnorderOtherList>
            {stakeholderOtherNameTh.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderOtherList>
          <UnorderList>
            {stakeholderNameEn.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                mt={index === 0 ? 2 : 0}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderList>
          <UnorderOtherList>
            {stakeholderOtherNameEn.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderOtherList>
        </Box>
      );
    },
  },
  {
    field: STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.EXPECTATION,
    headerName: 'ความคาดหวังของผู้มีส่วนได้เสีย',
    flex: 1,
    minWidth: 280,
    renderCell: (field) => {
      const { row, value } = field;

      const formattedThValue = convertNewlinesToHtml(value);
      const formattedEnValue = row.expectationDescriptionEn;

      return (
        <Box>
          <Typography dangerouslySetInnerHTML={{ __html: formattedThValue }} />
          <Typography
            mt={2}
            dangerouslySetInnerHTML={{ __html: formattedEnValue }}
          />
        </Box>
      );
    },
  },
  {
    field: STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.EXPECTATION_RESPONSE,
    headerName: 'การตอบสนองความคาดหวังของผู้มีส่วนได้เสีย',
    flex: 1,
    minWidth: 280,
    renderCell: (field) => {
      const { row, value } = field;

      const formattedThValue = convertNewlinesToHtml(value);
      const formattedEnValue = row.respondingToExpectationEn;

      return (
        <Box>
          <Typography dangerouslySetInnerHTML={{ __html: formattedThValue }} />
          <Typography
            mt={2}
            dangerouslySetInnerHTML={{ __html: formattedEnValue }}
          />
        </Box>
      );
    },
  },
  {
    field: STAKEHOLDER_ANALYSIS_TABLE_COLUMNS.CHANNEL,
    headerName: 'ช่องทางการมีส่วนร่วมและสื่อสาร',
    minWidth: 220,
    renderCell: (field) => {
      const { row, value } = field;

      const hasNoChannel = !value && !row.otherCommunicationActivities;
      const isEmptyChannel =
        value?.length === 0 && row.otherCommunicationActivities?.length === 0;

      if (hasNoChannel || isEmptyChannel) return <Typography>{'-'}</Typography>;

      const channelsTh = getContentList(value, 'TH');
      const communicationOtherNameTh = getContentList(
        row.otherCommunicationActivities ?? [],
        'TH'
      );
      const channelsEn = getContentList(value, 'EN');
      const communicationOtherNameEn = getContentList(
        row.otherCommunicationActivities ?? [],
        'EN'
      );
      return (
        <Box>
          <UnorderList>
            {channelsTh.map((channel, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: channel }}
              />
            ))}
          </UnorderList>
          <UnorderOtherList>
            {communicationOtherNameTh.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderOtherList>
          <UnorderList>
            {channelsEn.map((channel, index) => (
              <Typography
                component={'li'}
                key={index}
                mt={index === 0 ? 2 : 0}
                dangerouslySetInnerHTML={{ __html: channel }}
              />
            ))}
          </UnorderList>
          <UnorderOtherList>
            {communicationOtherNameEn.map((name, index) => (
              <Typography
                component={'li'}
                key={index}
                dangerouslySetInnerHTML={{ __html: name }}
              />
            ))}
          </UnorderOtherList>
        </Box>
      );
    },
  },
  {
    field: 'action',
    headerName: 'แก้ไข/ลบ',
    headerAlign: 'left',
    type: 'actions',
    minWidth: 80,
    getActions: (field) => {
      const { row } = field;
      if ((row.id.includes('DETAILS') || row.id.includes('NEW')) && props) {
        return [
          <StakeholderAnalysisActions
            key="actions"
            rowId={row.id}
            {...props}
          />,
        ];
      }
      return [];
    },
  },
];
